<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button type="button" class="btn invoice-back-button" :aria-label="$t('back')" @click="$router.go(-1)">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
          <span>{{$t('back')}}</span>
        </button>
        <div class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <h3 class="invoice-reference mb-0">{{ purchase.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button v-if="purchase.status != 'draft'" type="button" class="btn invoice-action-button invoice-action-button--primary" @click="ViewPurchase()">
              <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" aria-hidden="true" />
              <span>{{$t('view')}}</span>
            </button>
            <button v-if="helper.checkAccess('edit_purchases') != -1" type="button" class="btn invoice-action-button invoice-action-button--secondary" @click="editRow()">
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" />
              <span>{{$t('edit')}}</span>
            </button>
            <div v-if="purchase.status != 'draft' && helper.checkAccess('create_receptions') > 0" class="btn-group invoice-action-group">
              <button id="purchaseTransferButton" class="btn invoice-action-button invoice-action-button--secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" aria-hidden="true" />
                <span>{{$t('transfert')}}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="purchaseTransferButton">
                <li><button type="button" class="dropdown-item" @click="createReceptionModalState = true"><AppIcon name="bi-inbox" class="bi bi-inbox" /><span>{{$t('reception')}}</span></button></li>
              </ul>
            </div>
            <div v-if="purchase.status != 'draft' || helper.checkAccess('delete_purchases') != -1" class="btn-group invoice-action-group">
              <button id="purchaseMoreActionsButton" class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" :aria-label="$t('more')" :title="$t('more')">
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="purchaseMoreActionsButton">
                <li v-if="purchase.status != 'draft'"><button type="button" class="dropdown-item" @click="toggleReorder"><AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" /><span>{{$t('reorder')}}</span></button></li>
                <li v-if="purchase.status != 'draft' && helper.checkAccess('delete_purchases') != -1"><hr class="dropdown-divider"></li>
                <li v-if="helper.checkAccess('delete_purchases') != -1"><button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()"><AppIcon name="bi-trash" class="bi bi-trash" /><span>{{$t('delete')}}</span></button></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Purchase Info -->
      <div v-else-if="purchase && purchase.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_receptions') != -1">
            <a class="nav-link" :class="{active: step === 'receptions'}" @click="step = 'receptions'">{{$t('receptions')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_supplier_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row document-overview-grid">
            <!-- Left Column: Purchase Details -->
            <div class="col-md-8 document-overview-main">
              <div class="card mb-4 document-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal v-if="purchase && purchase.id" inline :data="purchase" type="purchase" />
                </div>
              </div>
              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4 document-products-card">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div v-if="reorderMode" class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" /> {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" /> {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('purchase_price')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th>{{$t('sub_total')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in purchase.products" :key="product.id">
                        <td><div class="product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{formatMoney(product.purchase_price)}}</td>
                        <td>{{product.quantity}}</td>
                        <td>{{formatMoney(product.sub_total)}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button type="button" class="btn product-move-button" @click="moveUp(index)" :disabled="index === 0"><AppIcon name="bi-arrow-up" class="bi bi-arrow-up" /></button>
                                <button type="button" class="btn product-move-button" @click="moveDown(index)" :disabled="index === purchase.products.length - 1"><AppIcon name="bi-arrow-down" class="bi bi-arrow-down" /></button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="purchase.notes" class="card document-notes-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ purchase.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Supplier & Totals Info -->
            <div class="col-md-4 document-overview-sidebar">
              <div class="card mb-4 document-info-card">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-bag" class="bi bi-bag me-2" />{{$t('purchase')}} {{$t('information')}}</h6></div>
                <div class="card-body"><div class="row">
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('reference')}}</small><strong>{{purchase.reference}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('date')}}</small><strong>{{formatDate(purchase.date)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_by')}}</small><strong>{{purchase.user ? purchase.user.name : '-'}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_at')}}</small><strong>{{formatDate(purchase.created_at)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('updated_at')}}</small><strong>{{formatDate(purchase.updated_at)}}</strong></div>
                </div></div>
              </div>
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-building" class="bi bi-building me-2" />{{$t('supplier')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!purchase.supplier" class="text-muted">{{$t('no_supplier_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{purchase?.supplier?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{purchase?.supplier?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{purchase?.supplier?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{purchase?.supplier?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Totals Card -->
              <!-- <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{$t('total')}}</h6>
                </div>
                <div class="card-body">
                  <div>
                    <small class="text-muted d-block">{{$t('total')}}</small>
                    <h4 class="text-primary mb-0">{{formatMoney(total)}}</h4>
                  </div>
                </div>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'receptions' && helper.checkAccess('view_receptions') != -1">
          <reception-component v-if="purchase && purchase.id" :products="purchase.products" :supplier="purchase.supplier" :purchase_id="this.purchase.id" :isDetailSupplier="true"></reception-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_supplier_invoices') != -1">
          <supplier-invoice-component v-if="purchase && purchase.id" :products="purchase.products" :supplier="purchase.supplier" :purchase_id="this.purchase.id" :isDetailSupplier="true"></supplier-invoice-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="purchase.id" entity_type="purchase" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>


        <!-- Edit Modal -->
        <add-edit-purchase-modal 
            v-if="editModalPurchaseisOpen" 
            @modalClosed="editModalPurchaseisOpen = false" 
            modalId="edit-purchase-modal-01" 
            :isModalOpen="editModalPurchaseisOpen"  
            :data="purchase" 
            action="update" 
        ></add-edit-purchase-modal>
        <!-- Edit modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalPurchaseIsOpen" 
            @modalClosed="invoiceModalPurchaseIsOpen = false" 
            modalId="invoice-purchase-modal-01" 
            :isModalOpen="invoiceModalPurchaseIsOpen"  
            :data="purchase" 
            type="purchase">
        </invoice-template-modal>
        <!-- quote modal -->


        <!-- Edit Modal -->
        <add-edit-reception-modal 
            v-if="createReceptionModalState" 
            @modalClosed="createReceptionModalState = false" 
            modalId="edit-reception-modal-01" 
            :isModalOpen="createReceptionModalState"  
            from="purchases"
            :data="purchase" 
            action="transfer" 
        ></add-edit-reception-modal>
        <!-- Edit modal -->

    </div>

    

</template>

<script>
import helper from '../../helper'
import ReceptionComponent from '../../components/ReceptionComponent.vue'
import moment from 'moment'
import attachments from '../../components/Attachment.vue'
import AddEditPurchaseModal from '../../components/purchase/AddEditPurchaseModal'
import AddEditReceptionModal from '../../components/reception/AddEditReceptionModal'
import SupplierInvoiceComponent from '../../components/SupplierInvoiceComponent.vue'
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';
import DocumentHistory from '../../components/DocumentHistory.vue';

export default {
  name: 'PurchaseDetails',
  components :{
    ReceptionComponent,
    attachments,
    AddEditPurchaseModal,
    InvoiceTemplateModal,
    AddEditReceptionModal,
    SupplierInvoiceComponent,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        purchase : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        invoiceModalPurchaseIsOpen : false,
        invoiceModalPurchase : null,
        editModalPurchase : null,
        editModalPurchaseisOpen : false,
        createReceptionModalState : false,
        step : 'overview',
        reorderMode: false,
        originalProductsOrder: null
    }
  },
  mounted(){
    window.mixpanel.track("purchase_details");

    //check permissions
    if(helper.checkAccess('view_purchases') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_purchases') == -2) return this.$router.push({path : '/subscribe'})

  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/purchases')
    this.getPurchase(this.$route.params.id)
  },
  methods : {
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
        async getPurchase(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/purchases/${id}`)
                this.purchase = response.data.purchase
                this.total = response.data.total
                this.amount_paid = response.data.amount_paid
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/purchases'})
                this.loading = false
            }
        },
        async getPurchaseHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/purchases/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;
            
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/purchases/${this.purchase.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        editRow(){
            this.editModalPurchaseisOpen = true;
            this.purchase.date = moment(this.purchase.date).format('YYYY-MM-DD')
            this.calculatePrices()
        },
        ViewPurchase(){
            this.invoiceModalPurchaseIsOpen = true;
        },
        async deleteRow(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
                }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/purchases/${this.purchase.id}`)
                    this.$router.push({path : '/purchases'})
                }
            })
        },
        async getReceptions(id){
            try{
                this.loading = true
                this.receptions = []
                const response = await helper.customGet(`${process.env.VUE_APP_API}/receptions?purchase_id=${id}`)
                this.receptions = response.data.data
                this.loading = false
            }catch(error){
                this.loading = false
            }

        },
        async showReceptions(purchase){
            this.purchase = purchase
            this.showReceptionsModalisOpen = true
            await this.getReceptions(purchase.id)
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.purchase.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.purchase.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.purchase.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.purchase.products = products;
            }
        },
        moveDown(index) {
            if (index < this.purchase.products.length - 1) {
                const products = [...this.purchase.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.purchase.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                const products = this.purchase.products.map(p => {
                    return {
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: p.custom_product,
                        discount: p.discount,
                        position: p.position
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/purchases/${this.purchase.id}`, {
                    reference: this.purchase.reference,
                    date: this.purchase.date,
                    supplier_id: this.purchase.supplier_id,
                    notes: this.purchase.notes,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getPurchase(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }
    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        helper(){ return helper},
        // total(){ return this.purchase.products.length ? this.purchase.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        sub_total(){ return this.purchase.products.length ? this.purchase.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
    },
    watch : {
        editModalisOpen(newValue) {
            if(newValue === false){
                this.getPurchase(this.$route.params.id)
            }
        },
        step(newValue) {
            if(newValue === 'history' && this.purchase.id){
                this.getPurchaseHistory(this.purchase.id)
            }
        }
    }
}
</script>

<style scoped>
    .product-rich-text::v-deep > :last-child {
        margin-bottom: 0 !important;
    }

    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
</style>
